import { CheckIcon, CircleIcon, LoaderCircleIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { FullPage, PulseLogo } from "@/components/full-page";
import { cn } from "@/lib/utils";

/** A serverless Azure SQL database usually resumes in about a minute. */
export const EXPECTED_WAKE_SECONDS = 60;

/**
 * Estimated progress (0–95%) after `elapsed` seconds. It eases toward the end so it reaches 90% at the expected
 * wake time and never claims to be finished: the screen goes away when the database actually answers.
 */
export function wakeProgress(elapsed: number): number {
  const tau = EXPECTED_WAKE_SECONDS / Math.log(10);
  return Math.min(95, 100 * (1 - Math.exp(-Math.max(0, elapsed) / tau)));
}

/** "About 45 seconds left" (rounded up to 5s), "A few seconds left", then "Taking a little longer than usual". */
export function wakeEstimate(elapsed: number): string {
  const remaining = EXPECTED_WAKE_SECONDS - elapsed;
  if (remaining <= 0) return "Taking a little longer than usual. Hang on…";
  if (remaining <= 10) return "A few seconds left…";
  return `About ${Math.ceil(remaining / 5) * 5} seconds left`;
}

function useElapsedSeconds(since: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, []);
  return Math.max(0, (now - since) / 1000);
}

type StepState = "done" | "active" | "todo";

function Step({ state, children }: { state: StepState; children: string }) {
  const Icon = state === "done" ? CheckIcon : state === "active" ? LoaderCircleIcon : CircleIcon;
  return (
    <li className={cn("flex items-center gap-2.5 text-sm", state === "todo" && "text-muted-foreground")}>
      <Icon
        className={cn(
          "size-4 shrink-0",
          state === "done" && "text-primary",
          state === "active" && "animate-spin text-primary",
          state === "todo" && "size-3.5 text-muted-foreground/60",
        )}
        aria-hidden="true"
      />
      <span>{children}</span>
      <span className="sr-only">{state === "done" ? "(done)" : state === "active" ? "(in progress)" : "(next)"}</span>
    </li>
  );
}

/** Shown at sign-in while the auto-paused database resumes; `since` is when Pulse first asked for it (ms). */
export function DatabaseWaking({ since }: { since: number }) {
  const elapsed = useElapsedSeconds(since);
  const progress = Math.round(wakeProgress(elapsed));
  return (
    <FullPage>
      <div className="rounded-xl border bg-card p-8 shadow-sm">
        <PulseLogo className="mx-auto mb-5 size-10 animate-pulse" />
        <h1 className="text-center text-base font-semibold">Waking up Pulse</h1>
        <p className="mt-1.5 text-center text-sm text-muted-foreground">
          Pulse's database goes to sleep after an hour without use, and it takes about a minute to start again. You
          don't need to do anything: this page carries on as soon as it's ready.
        </p>

        <div className="mt-6">
          <div
            className="h-2 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-label="Waking up the database"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress}
            aria-valuetext={wakeEstimate(elapsed)}
          >
            <div className="h-full rounded-full bg-primary transition-[width] duration-500 ease-out" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-2 flex justify-between text-[13px] text-muted-foreground tabular-nums">
            <span>{wakeEstimate(elapsed)}</span>
            <span>{Math.floor(elapsed)}s elapsed</span>
          </div>
        </div>

        <ol className="mt-6 space-y-2.5" aria-label="Sign-in progress">
          <Step state="done">Signed in with Microsoft</Step>
          <Step state="active">Starting the database</Step>
          <Step state="todo">Loading your workspace</Step>
        </ol>
      </div>
    </FullPage>
  );
}
